import type { MediaItem } from '@/types/posts';

/**
 * Frontend mirror of backend `validateInstagramAspectRatios` so we can warn
 * users at composition time instead of at publish time.
 *
 * Constants must stay in sync with backend
 * `instagram-publisher.service.ts:IG_ASPECT_LIMITS`. If those change, update
 * here too.
 *
 * Aspect ratio = width / height.
 */

export type InstagramContentType = 'FEED' | 'REELS' | 'STORIES';

export const IG_ASPECT_LIMITS: Record<InstagramContentType, { min: number; max: number }> = {
  FEED:    { min: 0.5625, max: 1.91 },   // 9:16 vertical → ~1.91:1 landscape (matches Meta's actual API range, empirically verified 2026-05-15)
  REELS:   { min: 0.5625, max: 1.0  },   // 9:16 vertical → 1:1 square
  STORIES: { min: 0.5625, max: 1.0  },
};

export interface IgAspectIssue {
  mediaId: string;
  width: number;
  height: number;
  aspect: number;
  reason: string;
}

export interface IgAspectCheckResult {
  ok: boolean;
  /** Aspect-out-of-range issues (only includes images with known dimensions). */
  issues: IgAspectIssue[];
  /** True when at least one image lacks dimensions; UI should stay quiet rather than warn. */
  hasUnknownDimensions: boolean;
}

/**
 * Check whether the given media items satisfy Instagram's aspect-ratio rules
 * for the given content type. Mirrors the backend guard's behaviour:
 * - Only IMAGE items are validated (videos are not aspect-checked).
 * - Items missing width/height are skipped (composer can't warn yet).
 * - Returns ALL violating items, not just the first — IG carousels reject
 *   the whole post if any one item is out of range.
 */
export function checkInstagramAspect(
  media: MediaItem[],
  contentType: InstagramContentType = 'FEED'
): IgAspectCheckResult {
  const limits = IG_ASPECT_LIMITS[contentType];
  const imageItems = media.filter(m => m.type === 'image');

  const issues: IgAspectIssue[] = [];
  let hasUnknownDimensions = false;

  for (const item of imageItems) {
    if (!item.width || !item.height) {
      hasUnknownDimensions = true;
      continue;
    }
    const aspect = item.width / item.height;
    if (aspect < limits.min || aspect > limits.max) {
      issues.push({
        mediaId: item.id,
        width: item.width,
        height: item.height,
        aspect,
        reason: `${item.width}×${item.height} (aspect ${aspect.toFixed(2)}) outside ${contentType} range ${limits.min}–${limits.max}`,
      });
    }
  }

  return {
    ok: issues.length === 0,
    issues,
    hasUnknownDimensions,
  };
}
